import React, { useState, useRef, useEffect } from 'react';

const MobileGestureHandler = ({ 
  children, 
  onSwipeLeft, 
  onSwipeRight, 
  onLongPress,
  swipeThreshold = 50,
  longPressThreshold = 500,
  className = ''
}) => {
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0, time: 0 });
  const [touchCurrent, setTouchCurrent] = useState({ x: 0, y: 0 });
  const [isLongPress, setIsLongPress] = useState(false);
  const elementRef = useRef(null);
  const longPressTimer = useRef(null);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    setTouchCurrent({ x: touch.clientX, y: touch.clientY });
    setIsLongPress(false);

    // Start long press timer
    if (onLongPress) {
      longPressTimer.current = setTimeout(() => {
        setIsLongPress(true);
        onLongPress(e);
        // Add haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }, longPressThreshold);
    }
  };

  const handleTouchMove = (e) => {
    if (!touchStart.x) return;

    const touch = e.touches[0];
    setTouchCurrent({ x: touch.clientX, y: touch.clientY });

    // Clear long press timer if user moves too much
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    
    if ((deltaX > 10 || deltaY > 10) && longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchStart.x || isLongPress) {
      setTouchStart({ x: 0, y: 0, time: 0 });
      setIsLongPress(false);
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      return;
    }

    const deltaX = touchCurrent.x - touchStart.x;
    const deltaY = touchCurrent.y - touchStart.y;
    const deltaTime = Date.now() - touchStart.time;

    // Check if it's a swipe (quick horizontal movement)
    if (
      Math.abs(deltaX) > swipeThreshold &&
      Math.abs(deltaY) < Math.abs(deltaX) / 2 &&
      deltaTime < 300
    ) {
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight(e);
        // Add haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(30);
        }
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft(e);
        // Add haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(30);
        }
      }
    }

    // Reset
    setTouchStart({ x: 0, y: 0, time: 0 });
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  return (
    <div
      ref={elementRef}
      className={className}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y' }} // Allow vertical scrolling
    >
      {children}
    </div>
  );
};

export default MobileGestureHandler;